// client/src/pages/PublicQuoteViewPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { fetchPublicQuote, signPublicQuote } from '../api/quotes';
import SignatureCanvas from 'react-signature-canvas';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCalendar, FiMapPin, FiMail, FiCheckCircle, FiAlertCircle, FiPenTool } from 'react-icons/fi';
import 'react-quill-new/dist/quill.snow.css';

const PublicQuoteViewPage = () => {
    const { publicAccessToken } = useParams();
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [typedName, setTypedName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [acceptanceSuccess, setAcceptanceSuccess] = useState(false);
    const [showReviseModal, setShowReviseModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const sigPad = useRef(null);

    useEffect(() => {
        const loadQuote = async () => {
            try {
                setLoading(true);
                const { quoteData } = await fetchPublicQuote(publicAccessToken);

                // DEBUG: Log the full quote data structure
                console.log('Full quote data:', quoteData);

                setQuote(quoteData); // This data is now camelCase

                if (quoteData.status === 'accepted') {
                    setAcceptanceSuccess(true);
                }
            } catch (err) {
                setError(err.message || 'Could not retrieve quote. The link may be invalid or expired.');
            } finally {
                setLoading(false);
            }
        };
        loadQuote();
    }, [publicAccessToken]);

    const handleAcceptance = async (e) => {
        e.preventDefault();
        if (!typedName.trim()) {
            alert('Please type your full name to accept the quote.');
            return;
        }
        if (sigPad.current.isEmpty()) {
            alert('Please provide your signature by drawing in the box.');
            return;
        }

        setIsSubmitting(true);
        setError('');
        try {
            const signatureDataUrl = sigPad.current.toDataURL();

            await signPublicQuote(publicAccessToken, {
                clientSignature: typedName,
                signatureDataUrl: signatureDataUrl
            });

            setAcceptanceSuccess(true);
            setQuote(prev => ({
                ...prev,
                status: 'accepted',
                signedByName: typedName,
                signatureDataUrl: signatureDataUrl,
                signedAt: new Date().toISOString()
            }));
        } catch (err) {
            setError(err.message || 'An error occurred while accepting the quote.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const clearSignature = () => {
        sigPad.current.clear();
    };

    const formatCurrency = (amount) => {
        const value = parseFloat(amount);
        if (isNaN(value)) return '$0.00';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    // Helper function to get terms a nd conditions from various possible locations
    const getTermsAndConditions = () => {
        // Check multiple possible locations for terms and conditions
        return quote.quoteTermsConditions ||
            quote.termsAndConditions ||
            quote.defaultTermsConditions ||
            quote.userCompany?.defaultTermsConditions ||
            quote.userCompany?.termsAndConditions ||
            null;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50">
                <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
                    <div className="text-red-500 text-5xl mb-4">
                        <FiAlertCircle className="mx-auto" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Unable to Load Quote</h2>
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    if (!quote) {
        return null;
    }

    // Calculate summary totals using correct camelCase properties
    let subtotal = quote.sections.reduce((acc, section) => acc + parseFloat(section.sectionSubtotal || 0), 0);
    let taxableSubtotal = quote.sections.reduce((acc, section) => acc + parseFloat(section.sectionTaxableSubtotal || 0), 0);

    // Add regular line items to totals
    if (Array.isArray(quote.regularLineItems)) {
        quote.regularLineItems.forEach(item => {
            const itemTotal = parseFloat(item.price) || 0;
            subtotal += itemTotal;
            if (item.isTaxable) {
                taxableSubtotal += itemTotal;
            }
        });
    }

    const discountAmount = quote.discountType === 'percentage'
        ? subtotal * parseFloat(quote.discountValue || 0)
        : parseFloat(quote.discountValue || 0);

    const taxAmount = taxableSubtotal * parseFloat(quote.overallTaxRate || 0);
    const termsAndConditions = getTermsAndConditions();

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut",
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <div className="min-h-screen font-sans py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
            {/* Background decoration */}
            <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-blue-100 blur-3xl opacity-50"></div>
                <div className="absolute top-[40%] -left-[10%] w-[40%] h-[40%] rounded-full bg-indigo-100 blur-3xl opacity-50"></div>
            </div>

            <motion.div
                className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-sm shadow-2xl rounded-2xl overflow-hidden border border-white/50"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Gradient Top Bar */}
                <div className="h-2 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>

                {/* --- Header Section --- */}
                <header className="bg-white/50 p-8 sm:p-12 border-b border-gray-100">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
                        <motion.div className="flex items-center gap-6" variants={itemVariants}>
                            {quote.userCompany?.companyLogoUrl ? (
                                <img
                                    src={quote.userCompany.companyLogoUrl}
                                    alt="Company Logo"
                                    className="h-20 w-auto object-contain"
                                />
                            ) : (
                                <div className="h-16 w-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                                    {quote.userCompany?.companyName?.charAt(0) || 'Q'}
                                </div>
                            )}
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                                    {quote.userCompany?.companyName || 'Service Provider'}
                                </h1>
                                <div className="mt-2 text-gray-500 text-sm space-y-1">
                                    <div className="flex items-center gap-2">
                                        <FiMail className="text-blue-500" />
                                        <span>{quote.userCompany?.email}</span>
                                    </div>
                                    {quote.userCompany?.phone && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-blue-500">📞</span>
                                            <span>{quote.userCompany.phone}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                        <motion.div className="text-left md:text-right" variants={itemVariants}>
                            <div className="inline-block bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-4 py-1.5 rounded-full text-sm font-bold tracking-wide uppercase mb-3 border border-blue-100 shadow-sm">
                                Quote #{quote.id}
                            </div>
                            <div className="space-y-1 text-sm text-gray-500">
                                <p className="flex items-center md:justify-end gap-2">
                                    <FiCalendar className="text-gray-400" />
                                    Issued: <span className="font-medium text-gray-900">{formatDate(quote.createdAt)}</span>
                                </p>
                                <p className="flex items-center md:justify-end gap-2">
                                    <FiCalendar className="text-gray-400" />
                                    Valid until: <span className="font-medium text-gray-900">{formatDate(quote.expirationDate)}</span>
                                </p>
                            </div>
                        </motion.div>
                    </div>

                    <motion.div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8" variants={itemVariants}>
                        <div className="bg-gray-50/80 rounded-xl p-6 border border-gray-100">
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                                <span className="w-1 h-1 rounded-full bg-gray-400"></span>
                                Prepared For
                            </h3>
                            <div className="text-gray-900">
                                <p className="text-xl font-bold">{quote.clientName}</p>
                                <div className="mt-3 space-y-2 text-gray-600 text-sm">
                                    <p className="flex items-center gap-2">
                                        <FiMail className="text-gray-400" />
                                        {quote.clientEmail}
                                    </p>
                                    {quote.clientAddress && (
                                        <p className="flex items-center gap-2">
                                            <FiMapPin className="text-gray-400" />
                                            {quote.clientAddress}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </header>

                {/* --- Main Content --- */}
                <main className="p-8 sm:p-12">
                    {quote.unifiedItems && quote.unifiedItems.length > 0 ? (
                        <div className="space-y-12">
                            {quote.unifiedItems.map((item, index) => {
                                if (item.type === 'section') {
                                    const section = item.data;
                                    return (
                                        <motion.div
                                            key={`section-${section.id || index}`}
                                            className="break-inside-avoid"
                                            variants={itemVariants}
                                        >
                                            <div className="flex justify-between items-baseline border-b border-gray-200 pb-4 mb-6">
                                                <h3 className="text-2xl font-bold text-gray-800">{section.name}</h3>
                                                <span className="text-xl font-semibold text-gray-900">{formatCurrency(section.sectionSubtotal || section.section_subtotal)}</span>
                                            </div>

                                            {section.description && (
                                                <div
                                                    className="prose prose-slate max-w-none text-gray-600 mb-6"
                                                    dangerouslySetInnerHTML={{ __html: formatDescription(section.description) }}
                                                />
                                            )}
                                        </motion.div>
                                    );
                                } else if (item.type === 'lineItem') {
                                    const lineItem = item.data;
                                    return (
                                        <motion.div
                                            key={`lineitem-${lineItem.id || index}`}
                                            className="flex justify-between items-start py-4 border-b border-gray-100 last:border-0 hover:bg-gray-50/50 transition-colors rounded-lg px-2 -mx-2"
                                            variants={itemVariants}
                                        >
                                            <div className="flex-1 pr-8">
                                                <div className="flex items-center gap-3 mb-1">
                                                    <h4 className="text-lg font-medium text-gray-900">{lineItem.itemName}</h4>
                                                    {lineItem.isTaxable && (
                                                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wider rounded">Taxable</span>
                                                    )}
                                                </div>
                                                {lineItem.description && (
                                                    <div
                                                        className="prose prose-sm max-w-none text-gray-500"
                                                        dangerouslySetInnerHTML={{ __html: formatDescription(lineItem.description) }}
                                                    />
                                                )}
                                            </div>
                                            <div className="text-right font-medium text-gray-900 whitespace-nowrap">
                                                {formatCurrency(lineItem.price)}
                                            </div>
                                        </motion.div>
                                    );
                                }
                                return null;
                            })}
                        </div>
                    ) : (
                        // Fallback for old quotes
                        <div className="space-y-12">
                            {quote.sections && quote.sections.map((section, index) => (
                                <motion.div
                                    key={section.id || index}
                                    className="break-inside-avoid"
                                    variants={itemVariants}
                                >
                                    <div className="flex justify-between items-baseline border-b border-gray-200 pb-4 mb-6">
                                        <h3 className="text-2xl font-bold text-gray-800">{section.name}</h3>
                                        <span className="text-xl font-semibold text-gray-900">{formatCurrency(section.sectionSubtotal)}</span>
                                    </div>
                                    {section.description && (
                                        <div
                                            className="prose prose-slate max-w-none text-gray-600"
                                            dangerouslySetInnerHTML={{ __html: formatDescription(section.description) }}
                                        />
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    )}
                </main>

                {/* --- Footer & Totals --- */}
                <div className="bg-gray-50/50 p-8 sm:p-12 border-t border-gray-200">
                    <motion.div className="flex flex-col md:flex-row justify-end" variants={itemVariants}>
                        <div className="w-full md:w-1/2 lg:w-1/3 space-y-4">
                            <div className="flex justify-between text-gray-600">
                                <span>Subtotal</span>
                                <span className="font-medium text-gray-900">{formatCurrency(subtotal)}</span>
                            </div>
                            {discountAmount > 0 && (
                                <div className="flex justify-between text-green-600">
                                    <span>Discount</span>
                                    <span className="font-medium">- {formatCurrency(discountAmount)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-gray-600">
                                <span>Tax ({(parseFloat(quote.overallTaxRate || 0) * 100).toFixed(2)}%)</span>
                                <span className="font-medium text-gray-900">{formatCurrency(taxAmount)}</span>
                            </div>
                            <div className="pt-6 border-t border-gray-200 flex justify-between items-center">
                                <span className="text-lg font-bold text-gray-900">Total</span>
                                <span className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                                    {formatCurrency(quote.grandTotal)}
                                </span>
                            </div>
                        </div>
                    </motion.div>

                    {/* Terms */}
                    {termsAndConditions && (
                        <motion.div className="mt-16 pt-8 border-t border-gray-200" variants={itemVariants}>
                            <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Terms & Conditions</h4>
                            <div className="text-sm text-gray-500 whitespace-pre-wrap leading-relaxed bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                                {termsAndConditions}
                            </div>
                        </motion.div>
                    )}

                    {/* Acceptance Section */}
                    <motion.div className="mt-16" variants={itemVariants}>
                        {acceptanceSuccess ? (
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="bg-green-50 border border-green-200 rounded-xl p-8 text-center"
                            >
                                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                                    <FiCheckCircle className="w-10 h-10 text-green-600" />
                                </div>
                                <h3 className="text-3xl font-bold text-green-900 mb-2">Quote Accepted!</h3>
                                <p className="text-green-700 mb-8 text-lg">
                                    Signed by <span className="font-semibold">{quote.signedByName}</span> on {formatDate(quote.signedAt)}
                                </p>
                                <div className="inline-block bg-white p-6 rounded-xl border border-green-100 shadow-sm transform rotate-1 hover:rotate-0 transition-transform duration-300">
                                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-2 text-left">Signature</p>
                                    <img src={quote.signatureDataUrl} alt="Client Signature" className="h-24 w-auto" />
                                </div>
                            </motion.div>
                        ) : (
                            <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
                                <div className="p-6 sm:p-8 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                        <FiPenTool className="text-blue-500" />
                                        Acceptance
                                    </h3>
                                    <p className="text-gray-500 mt-1">Please review the quote and sign below to proceed.</p>
                                </div>

                                <div className="p-6 sm:p-8">
                                    <form onSubmit={handleAcceptance} className="space-y-6">
                                        <div>
                                            <label htmlFor="typedName" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                            <input
                                                type="text"
                                                id="typedName"
                                                value={typedName}
                                                onChange={(e) => setTypedName(e.target.value)}
                                                className="block w-full px-4 py-3 rounded-lg border-gray-300 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                placeholder="e.g. John Doe"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Signature</label>
                                            <div className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:bg-white transition-colors touch-none relative group">
                                                <SignatureCanvas
                                                    ref={sigPad}
                                                    penColor='black'
                                                    canvasProps={{ className: 'w-full h-48 rounded-lg' }}
                                                />
                                                <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    Draw here
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={clearSignature}
                                                    className="absolute top-2 right-2 text-xs text-gray-400 hover:text-red-500 bg-white px-2 py-1 rounded shadow-sm border border-gray-200 transition-colors"
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                            <p className="text-xs text-gray-400 mt-2">Draw your signature above</p>
                                        </div>

                                        <div className="pt-4">
                                            <motion.button
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="w-full flex justify-center py-4 px-6 border border-transparent rounded-xl shadow-lg text-lg font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                            >
                                                {isSubmitting ? (
                                                    <span className="flex items-center">
                                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Processing...
                                                    </span>
                                                ) : 'Accept & Sign Quote'}
                                            </motion.button>
                                        </div>
                                    </form>

                                    <div className="mt-8 grid grid-cols-2 gap-4">
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => setShowReviseModal(true)}
                                            className="flex justify-center items-center py-3 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                        >
                                            Request Revisions
                                        </motion.button>
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => setShowRejectModal(true)}
                                            className="flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                        >
                                            Reject Quote
                                        </motion.button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </motion.div>
                </div>
            </motion.div>

            <div className="text-center py-8">
                <p className="text-sm text-gray-400 font-medium">Powered by <a href="/" className="text-gray-600 font-bold hover:text-blue-600 transition-colors">SwiftBidPro</a></p>
            </div>

            {/* Revise Modal */}
            <AnimatePresence>
                {showReviseModal && (
                    <ReviseQuoteModal
                        publicAccessToken={publicAccessToken}
                        onClose={() => setShowReviseModal(false)}
                        onSuccess={() => {
                            setShowReviseModal(false);
                            alert('Thank you! Your revision request has been sent.');
                        }}
                    />
                )}
            </AnimatePresence>

            {/* Reject Modal */}
            <AnimatePresence>
                {showRejectModal && (
                    <RejectQuoteModal
                        publicAccessToken={publicAccessToken}
                        onClose={() => setShowRejectModal(false)}
                        onSuccess={() => {
                            setShowRejectModal(false);
                            alert('Thank you for your feedback. Your rejection has been recorded.');
                        }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

const ReviseQuoteModal = ({ publicAccessToken, onClose, onSuccess }) => {
    const [feedback, setFeedback] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!feedback.trim()) {
            setError('Please provide your feedback.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const response = await fetch(`/api/quotes/public/${publicAccessToken}/request-revision`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ feedback })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to request revision');
            }

            onSuccess();
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 overflow-auto p-4"
        >
            <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-lg relative overflow-hidden"
            >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-orange-500"></div>
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">Request Revisions</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors p-1 rounded-full hover:bg-gray-100">
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <p className="text-gray-600 mb-6">Please let us know what changes you'd like to see in this quote. We'll get back to you with an updated version.</p>

                <form onSubmit={handleSubmit}>
                    <div className="mb-6">
                        <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
                            Your feedback
                        </label>
                        <textarea
                            id="feedback"
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors resize-none"
                            placeholder="e.g. The quantity for item A should be 5 instead of 3..."
                            rows="5"
                            required
                        />
                        {error && <p className="text-red-500 text-sm mt-2 flex items-center"><FiAlertCircle className="mr-1" />{error}</p>}
                    </div>

                    <div className="flex gap-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-3 px-4 border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 py-3 px-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                        >
                            {isSubmitting ? 'Sending...' : 'Submit Request'}
                        </button>
                    </div>
                </form>
            </motion.div>
        </motion.div>
    );
};

const RejectQuoteModal = ({ publicAccessToken, onClose, onSuccess }) => {
    const [feedback, setFeedback] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!feedback.trim()) {
            setError('Please provide your reason for rejecting.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const response = await fetch(`/api/quotes/public/${publicAccessToken}/reject`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ feedback })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to reject quote');
            }

            onSuccess();
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 overflow-auto p-4"
        >
            <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-lg relative overflow-hidden"
            >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-pink-600"></div>
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">Reject Quote</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors p-1 rounded-full hover:bg-gray-100">
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <p className="text-gray-600 mb-6">We're sorry to hear that. We'd appreciate your feedback on why you're rejecting this proposal so we can improve.</p>

                <form onSubmit={handleSubmit}>
                    <div className="mb-6">
                        <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
                            Reason for rejection
                        </label>
                        <textarea
                            id="feedback"
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors resize-none"
                            placeholder="e.g. The price is too high..."
                            rows="5"
                            required
                        />
                        {error && <p className="text-red-500 text-sm mt-2 flex items-center"><FiAlertCircle className="mr-1" />{error}</p>}
                    </div>

                    <div className="flex gap-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-3 px-4 border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 py-3 px-4 bg-gradient-to-r from-red-600 to-pink-600 text-white font-semibold rounded-xl hover:from-red-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                        >
                            {isSubmitting ? 'Sending...' : 'Reject Quote'}
                        </button>
                    </div>
                </form>
            </motion.div>
        </motion.div>
    );
};

const formatDescription = (description) => {
    if (!description) return '';

    // Apply styling to unordered lists - let CSS handle nested levels
    let formatted = description.replace(/<ul[^>]*>/g, '<ul>');

    // Apply styling to ordered lists - let CSS handle nested levels
    formatted = formatted.replace(/<ol[^>]*>/g, '<ol>');

    // Remove inline styles from list items that might override our CSS
    formatted = formatted.replace(/<li[^>]*style="[^"]*"[^>]*>/g, (match) => {
        return match.replace(/style="[^"]*"/g, '');
    });

    // Clean up any Quill-specific classes that might interfere
    formatted = formatted.replace(/class="ql-indent-\d+"/g, '');

    return formatted;
};

export default PublicQuoteViewPage;
